import { useAuth } from '@/contexts/auth-context';
import { isOnFreePlan } from '@/guards/free-plan-access';
import { useLastOnboardingDnaGeneration } from '@/hooks/actions/use-onboarding-dna-action';
import { useBrandDnaQuery } from '@/hooks/queries/use-brand-dna-query';
import { useDiscoveryPreferencesQuery } from '@/hooks/queries/use-discovery-preferences-query';
import { dnaPanelStatus } from '../_components/onboarding-dna';
import type { OnboardingSnapshot } from '../_components/onboarding-snapshot';
import { isSalesChannelConnected, type OnboardingStep } from '../_components/onboarding-steps';

/** What a step is typing and has not saved yet. Absent fields read the server. */
export type OnboardingLiveAnswers = Partial<
  Pick<
    OnboardingSnapshot,
    'brandName' | 'websiteUrl' | 'creatorPreferences' | 'collaborationTypes' | 'salesChannel'
  >
>;

export function useOnboardingSnapshot(
  step: OnboardingStep,
  live: OnboardingLiveAnswers = {}
): OnboardingSnapshot {
  const { user } = useAuth();
  const brand = user?.currentBrand;
  const { data: dna } = useBrandDnaQuery();
  const generation = useLastOnboardingDnaGeneration();
  const { data: savedPreferences } = useDiscoveryPreferencesQuery(user?.currentBrandId ?? '');

  const websiteUrl = live.websiteUrl !== undefined ? live.websiteUrl : (brand?.websiteUrl ?? null);
  const salesChannel =
    live.salesChannel !== undefined ? live.salesChannel : (brand?.onboarding?.salesChannel ?? null);
  const savedDna = dna?.brandDna ?? null;

  return {
    brandName: live.brandName ?? brand?.name ?? '',
    websiteUrl,
    dnaStatus: dnaPanelStatus({
      websiteUrl,
      savedDna,
      generation,
      analysisExpected: step === 'brand',
    }),
    dnaText: savedDna,
    creatorPreferences:
      live.creatorPreferences !== undefined ? live.creatorPreferences : (savedPreferences ?? null),
    collaborationTypes: live.collaborationTypes ?? brand?.onboarding?.collaborationTypes ?? [],
    salesChannel,
    isStoreConnected: isSalesChannelConnected(
      salesChannel,
      !!user?.integrations?.shopify?.isConnected
    ),
    isFreePlan: isOnFreePlan(user),
  };
}
